import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FiUser, FiMail, FiShield, FiLock } from 'react-icons/fi';
import api from '../services/api';

const Profile = () => {
  const { user } = useAuth();
  const [changingPassword, setChangingPassword] = useState(false);
  
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm();

  const onPasswordSubmit = async (data) => {
    if (data.new_password !== data.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      await api.put('/auth/change-password', {
        current_password: data.current_password,
        new_password: data.new_password,
      });
      
      toast.success('Mot de passe modifié avec succès');
      setChangingPassword(false);
      resetPassword();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la modification du mot de passe');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
      directeur: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200',
      secretaire: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
      enseignant: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
      comptable: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
      surveillant: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    };
    return colors[role] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Mon Profil</h1>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        {/* User Info */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Informations personnelles</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center space-x-3">
              <FiUser className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Nom complet</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{user?.nom} {user?.prenom}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <FiMail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{user?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <FiShield className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Rôle</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user?.role)}`}>
                  {user?.role}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <FiUser className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Nom d'utilisateur</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{user?.username}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Permissions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {user?.permissions?.map((permission) => (
              <div key={permission} className="flex items-center space-x-2">
                <div className="h-2 w-2 bg-green-500 dark:bg-green-400 rounded-full"></div>
                <span className="text-sm text-gray-600 dark:text-gray-300">{permission}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Change Password */}
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Sécurité</h2>
          
          {!changingPassword ? (
            <button
              onClick={() => setChangingPassword(true)}
              className="flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              <FiLock />
              <span>Changer le mot de passe</span>
            </button>
          ) : (
            <form onSubmit={handleSubmitPassword(onPasswordSubmit)} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Mot de passe actuel
                </label>
                <input
                  {...registerPassword('current_password', { required: 'Mot de passe actuel requis' })}
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                {passwordErrors.current_password && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{passwordErrors.current_password.message}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nouveau mot de passe
                </label>
                <input
                  {...registerPassword('new_password', { 
                    required: 'Nouveau mot de passe requis',
                    minLength: { value: 6, message: 'Le mot de passe doit contenir au moins 6 caractères' }
                  })}
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                {passwordErrors.new_password && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{passwordErrors.new_password.message}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirmer le mot de passe
                </label>
                <input
                  {...registerPassword('confirm_password', { required: 'Confirmation requise' })}
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                {passwordErrors.confirm_password && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{passwordErrors.confirm_password.message}</p>
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
                >
                  Modifier
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChangingPassword(false);
                    resetPassword();
                  }}
                  className="bg-gray-200 dark:bg-gray-700 dark:text-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Annuler
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;